import React, { useEffect, useRef } from "react"
import { useRouter } from "next/router"
import { create } from "zustand/react"
import useCustomStore from "../hooks/useCustomStore"
import useIsDebug from "../hooks/useIsDebug"
import useMediaQuery from "../hooks/useMediaQuery"
import { precise, setCssVars } from "@repo/utils"
import { isChrome, isFirefox, isIOS } from "react-device-detect"
import { useDevice } from "./DeviceProvider"

const INIT_VIEWPORT = Object.freeze({
	width: 0,
	height: 0,
	sHeight: 0,
	lHeight: 0,
})

function getVH() {
	const testEl = document.createElement("div")
	testEl.style.position = "absolute"
	testEl.style.visibility = "hidden"
	testEl.style.height = "100lvh"
	document.body.appendChild(testEl)

	const lvh = parseFloat(getComputedStyle(testEl).height)

	testEl.style.height = "100svh"
	const svh = parseFloat(getComputedStyle(testEl).height)

	document.body.removeChild(testEl)

	return [svh, lvh]
}

const Debug = () => {
	const viewport = useViewport()
	return (
		<div
			style={{
				position: "fixed",
				bottom: 50,
				right: 30,
				background: "crimson",
				minWidth: 100,
				zIndex: 100,
				padding: 10,
				display: "flex",
				gap: 10,
				flexDirection: "column",
			}}
		>
			<p>width:{viewport.width}px</p>
			<p>height:{viewport.height}px</p>
			<p>lHeight:{viewport.lHeight}px</p>
			<p>sHeight:{viewport.sHeight}px</p>
		</div>
	)
}

function calculateVH(height) {
	return precise(height / 100, 2)
}

function useDynamicVH() {
	const viewport = useViewport()
	const { height, sHeight, lHeight } = viewport
	const init = useRef(false)
	const values = useRef({
		vh: 0,
		svh: 0,
		lvh: 0,
	})
	const orientation = useDevice("orientation")

	useEffect(() => {
		return () => {
			//reset a flag so in case it is chromium values get update on orientation change
			init.current = false
		}
	}, [orientation])

	useEffect(() => {
		const isChromium = isChrome || isFirefox
		let type
		if (isIOS && isChromium) {
			if (init.current === true) return
			const height = calculateVH(window.innerHeight)
			values.current = {
				vh: height,
				svh: height,
				lvh: height,
			}
			type = "custom"
		} else {
			values.current = {
				vh: calculateVH(height),
				svh: calculateVH(sHeight),
				lvh: calculateVH(lHeight),
			}
			type = "classic"
		}

		setCssVars(document.documentElement, {
			"custom-vh": `${values.current.vh}px`,
			"custom-svh": `${values.current.svh}px`,
			"custom-lvh": `${values.current.lvh}px`,
			vh: `var(--${type}-vh)`,
			svh: `var(--${type}-svh)`,
			lvh: `var(--${type}-lvh)`,
		})

		init.current = true
	}, [height, sHeight, lHeight])

	return viewport
}

const useViewportStore = create((set) => ({
	store: { ...INIT_VIEWPORT },
	setViewport: (dimensions) =>
		set((state) => ({
			...state,
			store: {
				...state.store,
				...dimensions,
			},
		})),
}))

export default function ViewportProvider({ children }) {
	const setViewport = useViewportStore((state) => state.setViewport)
	const orientation = useDevice("orientation")
	const isDebug = useIsDebug()
	const router = useRouter()

	useEffect(() => {
		const handleResize = ({ vh = false }) => {
			let updated = { width: window.innerWidth, height: window.innerHeight }
			if (vh) {
				const [svh, lvh] = getVH()
				updated.sHeight = svh
				updated.lHeight = lvh
			}
			setViewport(updated)
		}

		handleResize({ vh: true })

		const controller = new AbortController()
		window.addEventListener("resize", handleResize, { signal: controller.signal })

		return () => controller.abort()
	}, [orientation, setViewport])

	useEffect(() => {
		const handleOrientationChange = () => {
			router.reload()
		}

		window.addEventListener("orientationchange", handleOrientationChange)

		return () => {
			window.removeEventListener("orientationchange", handleOrientationChange)
		}
	}, [router])

	useDynamicVH()

	return (
		<>
			{children}
			{isDebug && <Debug />}
		</>
	)
}

export function useViewport(selector) {
	return useCustomStore(useViewportStore, selector)
}
